/**
 * @file error-mixin.ts
 *
 * Owns error capture, normalization, and server-side logging. Registers
 * global window 'error' and 'unhandledrejection' handlers that surface errors
 * through the modal overlay and log them to the server. Also sends an
 * 'app_closed' beacon on 'beforeunload'.
 *
 * sendLog suppresses all server calls in localDev / iframe / noAPI
 * environments; in localDev with debug-log-notifications it emits a visible
 * as-notify event instead.
 *
 * Requires: ModalMixinMembers (for showErrorModal)
 * Exposes: logError, sendLog, errorMessage
 */
import { sendLogToServer, sendBeaconToServer } from '../../../features/serverApi/asApi.ts';
import { localDev, inIframe, noAPI, isOnPrem } from '../../../data/constants.ts';
import { devlog } from '../../../util/utils.ts';
import { type Constructor } from './mixin-types-utils.ts';
import type { ModalMixinMembers } from './modal-mixin.ts';
import type { PhpApiError } from '../../../data/data-interfaces.ts';
import { currentProjectData } from '../../../data/projectManagement.ts';
import { internalData } from '../../../store/store.ts';
import { sendEv } from '../../../data/events.ts';

/** Surface added by ErrorMixin. Referenced by ProjectMixin and TransferMixin for
 *  structured error reporting and server-side event logging. */
export interface ErrorMixinMembers {
  logError(origin: string, err: unknown, importance?: 'critical' | 'regular' | 'warning', label?: string): void;
  /** Second arg may be a subcategory string (then `data` is the payload) or the payload object itself.
   *  Always resolves — the server call's own rejection is already caught internally — so callers that
   *  fire-and-forget can still attach a `.catch()` defensively without it ever actually firing. */
  sendLog(evName: string, subcategoryOrData?: string | object, data?: object): Promise<void>;
  errorMessage(err: unknown): string;
}

export const ErrorMixin = <T extends Constructor<HTMLElement & ModalMixinMembers>>(Base: T) =>
  class ErrorMixin extends Base {
    _isAbortError(err: unknown): boolean {
      if (err instanceof Error) {
        if (err.name === 'AbortError') return true;
        if (/\bstatus 499\b/.test(err.message)) return true;
        if (/"status"\s*:\s*499\b/.test(err.message)) return true;
      }
      if (typeof err === 'string') {
        if (/\bstatus 499\b/.test(err)) return true;
        if (/"status"\s*:\s*499\b/.test(err)) return true;
      }
      // Plain object from PHP/Paramate SDK: PhpApiError shape
      if (err !== null && typeof err === 'object') {
        const e = err as PhpApiError;
        if (e.status === 499) return true;
        if (typeof e.response?.message === 'string' && e.response.message.toLowerCase().includes('aborted')) return true;
      }
      return false;
    }

    _notifyAborted(): void {
      sendEv(this, 'as-notify', {
        bubbles: false,
        detail: {
          defaultText: "Operation aborted. If you didn't cancel, your connection may be unstable.",
          dictKey: 'notifications.error.aborted',
          type: 'warning',
        }
      });
    }

    /**
     * Browser-injected user scripts (e.g. Firefox iOS's password-manager
     * content script — confirmed via `window.__firefox__` — it runs in the
     * *same* JS realm as the page, unlike an isolated-world browser
     * extension) report a special, non-URL `filename` like "user-script"
     * instead of an actual http(s) source when they throw. An uncaught
     * error in that code is indistinguishable from one of ours at the
     * `window.onerror` level, but we have no control over it and can't fix
     * it — logging it and showing our own error modal for someone else's
     * bug is actively misleading rather than useful.
     */
    _isThirdPartyScriptError(event: ErrorEvent): boolean {
      return !/^https?:\/\//i.test(event.filename ?? '');
    }

    _handleWindowError = (event: ErrorEvent): void => {
      if (event.message?.includes('ResizeObserver loop completed with undelivered notifications')) return;
      if (this._isAbortError(event.error) || /\bstatus 499\b/.test(event.message ?? '')) {
        this._notifyAborted();
        return;
      }
      if (this._isThirdPartyScriptError(event)) {
        devlog('Ignoring error from a non-app script', event);
        return;
      }
      devlog('Got error', event);
      this.sendLog('error', {
        origin: 'uncaught',
        message: event.message ?? this.errorMessage(event.error),
        file: event.filename,
        line: event.lineno,
        col: event.colno,
        importance: 'critical',
      });
      this.showErrorModal({ error: event.error, origin: 'uncaught' });
    };

    _handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
      const reason = event.reason;
      if (this._isAbortError(reason)) {
        this._notifyAborted();
        return;
      }
      const message = this.errorMessage(reason);
      devlog('Unhandled promise rejection', reason);
      this.sendLog('error', { origin: 'unhandled-rejection', message, stack: reason?.stack ?? '', importance: 'critical' });
      this.showErrorModal({ error: reason instanceof Error ? reason : new Error(message), origin: 'unhandled-rejection' });
    };

    _handleAsLogError = (event: Event): void => {
      const { origin, error, importance = 'regular', notify, notifyDictKey, notifyVars } = (event as CustomEvent).detail ?? {};
      this.logError(origin ?? 'unknown', error, importance);
      if (notify) {
        sendEv(this, 'as-notify', {
          bubbles: false,
          detail: { defaultText: notify, dictKey: notifyDictKey, vars: notifyVars, type: 'error' }
        });
      }
    };

    _handleBeforeUnload = (): void => {
      if (localDev || isOnPrem || inIframe || noAPI) return;
      const sessionId = currentProjectData.sessionId || undefined;
      sendBeaconToServer(
        internalData.appId === 'storefront' ? 'storefront_closed' : 'app_closed',
        sessionId ? { sessionId } : undefined
      );
    };

    /** A button named `open-modal` anywhere in the app shows the standing error
     *  panel. Documented for app authors, so it stays a global name. */
    _handleOpenModalButton = (event: Event): void => {
      if ((event as CustomEvent).detail?.buttonName === 'open-modal') {
        this.modalOverlay?.open('main-error-panel');
      }
    };

    async connectedCallback() {
      await super.connectedCallback?.();
      window.addEventListener('as-button-click', this._handleOpenModalButton);
      window.addEventListener('error', this._handleWindowError as EventListener);
      window.addEventListener('unhandledrejection', this._handleUnhandledRejection as EventListener);
      window.addEventListener('beforeunload', this._handleBeforeUnload as EventListener);
      window.addEventListener('as-log-error', this._handleAsLogError as EventListener);
    }

    disconnectedCallback() {
      window.removeEventListener('as-button-click', this._handleOpenModalButton);
      window.removeEventListener('error', this._handleWindowError as EventListener);
      window.removeEventListener('unhandledrejection', this._handleUnhandledRejection as EventListener);
      window.removeEventListener('beforeunload', this._handleBeforeUnload as EventListener);
      window.removeEventListener('as-log-error', this._handleAsLogError as EventListener);
      super.disconnectedCallback?.();
    }

    errorMessage(err: unknown): string {
      if (err instanceof Error) return err.message;
      if (typeof err === 'string') return err;
      if (err === undefined || err === null) return String(err);
      try { return JSON.stringify(err) ?? String(err); } catch { return String(err); }
    }

    public logError(origin: string, err: unknown, importance: 'critical' | 'regular' | 'warning' = 'regular', label?: string): void {
      console.error(label ?? `[AS-APP] ${origin}:`, err);
      this.sendLog('error', { origin, message: this.errorMessage(err), importance });
    }

    sendLog(evName: string, subcategoryOrData?: string | object, data?: object): Promise<void> {
      // Backward-compatible signature: the second arg is an optional subcategory
      // string (e.g. 'local' | 'interapp' | 'grabcad' for data-import/data-export),
      // in which case `data` carries the payload. If it's an object, it IS the
      // payload and there is no subcategory (legacy two-arg form).
      const subcategory = typeof subcategoryOrData === 'string' ? subcategoryOrData : undefined;
      const body = (typeof subcategoryOrData === 'string' ? data : subcategoryOrData) as object | undefined;
      const sessionId = currentProjectData.sessionId || undefined;
      const payload = sessionId
        ? { sessionId, ...body }
        : body;
      if (localDev || isOnPrem || inIframe || noAPI) {
        if (!inIframe && !noAPI && this.hasAttribute('debug-log-notifications')) {
          const sub = subcategory ? `/${subcategory}` : '';
          const details = payload ? ` | ${JSON.stringify(payload)}` : '';
          sendEv(this, 'as-notify', {
            bubbles: false,
            detail: { defaultText: `Log event (not sent): ${evName}${sub}${details}`, type: 'warning' }
          });
        }
        return Promise.resolve();
      }
      return sendLogToServer(evName, payload, subcategory).catch(console.error);
    }
  };
